/**
 * Hook for exporting MSSQL databases to .bacpac files.
 * Uses TanStack Query mutation for async state management.
 */

import { useMutation } from '@tanstack/react-query';
import { mssqlExport } from '../services/backupRestoreApi';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import type { MssqlExportRequest, MssqlExportResult } from '../types/backupRestore';

type UseBackupExportOptions = {
  onSuccess?: (result: MssqlExportResult, operationId: string) => void;
  onError?: (error: Error) => void;
};

/**
 * Hook for initiating MSSQL .bacpac export operations.
 * Tracks progress in the backup/restore store.
 */
export function useBackupExport(options: UseBackupExportOptions = {}) {
  const { addOperation, updateOperation } = useBackupRestoreStore();

  const mutation = useMutation({
    mutationFn: async (request: MssqlExportRequest): Promise<{ result: MssqlExportResult; operationId: string }> => {
      // Add operation to store before starting
      const operationId = addOperation({
        type: 'export',
        connectionId: request.connectionId,
        databaseName: request.databaseName,
        filePath: request.filePath || '', // May be empty if dialog not yet shown
      });

      try {
        // Update status to in progress
        updateOperation(operationId, { status: 'inProgress' });

        const result = await mssqlExport(request);

        if (result.success) {
          // Update with task ID for progress tracking
          updateOperation(operationId, {
            taskId: result.taskId,
            status: 'inProgress',
            message: 'Export started...',
          });
        } else if (result.cancelled) {
          // User cancelled the save dialog
          updateOperation(operationId, {
            status: 'cancelled',
            completedAt: new Date().toISOString(),
          });
        } else {
          // Export failed
          updateOperation(operationId, {
            status: 'failed',
            error: result.error,
            completedAt: new Date().toISOString(),
          });
        }

        return { result, operationId };
      } catch (error) {
        updateOperation(operationId, {
          status: 'failed',
          error: error instanceof Error ? error.message : 'Unknown error',
          completedAt: new Date().toISOString(),
        });
        throw error;
      }
    },
    onSuccess: (data) => {
      options.onSuccess?.(data.result, data.operationId);
    },
    onError: (error: Error) => {
      options.onError?.(error);
    },
  });

  return {
    exportDatabase: mutation.mutate,
    exportDatabaseAsync: mutation.mutateAsync,
    isExporting: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
